import {
  Box,
  Flex,
  Square,
  Text,
  useColorModeValue,
  Tooltip,
  Input,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { EventGroupOrCheckpoint } from '../ProjectHistoryTimeline';
import TimelineDot from './TimelineDot';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { useEventHistory } from 'src/hooks/event-history';
import { EventResource, EventType } from 'src/gql/graphql';

interface CurrentVersionHeaderProps {
  label?: string;
  isSelected?: boolean;
  isInPreview?: boolean;
  isLastInSelection?: boolean;
  isLastInPreview?: boolean;
  onClick?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  projectId: string;
  isNextEventCheckpoint: boolean;
  reloadEventGroups: () => Promise<EventGroupOrCheckpoint[]>;
}

// eslint-disable-next-line complexity
export const CurrentVersionHeader: React.FC<CurrentVersionHeaderProps> = ({
  label = 'Current version',
  isSelected = false,
  isInPreview = false,
  isLastInSelection = false,
  isLastInPreview = false,
  projectId,
  isNextEventCheckpoint,
  onClick,
  onMouseDown,
  reloadEventGroups,
}) => {
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);
  const [checkPointName, setCheckpointName] = useState('');

  // Colors - same pattern as TimelineEventWrapper
  const currentVersionColor = useColorModeValue('cyan.500', 'cyan.200');
  const selectedBgColor = useColorModeValue('blue.50', 'blue.800');
  const previewBgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');

  const { t } = useTranslation('project-history');
  const { logEvent } = useEventHistory(projectId);

  const handleCheckPointCreation = useCallback(async () => {
    setIsAddingCheckpoint(false);
    invariant(checkPointName, 'Check point name is missing');
    await logEvent({
      eventName: checkPointName,
      eventType: EventType.Created,
      resourceType: EventResource.Project,
      resourceName: checkPointName,
      isCheckpoint: true,
      metadata: {
        checkPointName,
      },
    });
    await reloadEventGroups();
  }, [logEvent, checkPointName, reloadEventGroups]);

  // Simple background color: preview > selected > none
  const bgColor = isInPreview
    ? previewBgColor
    : isSelected
    ? selectedBgColor
    : undefined;

  // Border styles
  const hasBorder = isSelected || isInPreview;
  const isLastItem = isInPreview ? isLastInPreview : isLastInSelection;
  // Show top border when selected/preview (always show when selected, even if only item)
  const showTopBorder = hasBorder;

  return (
    <Flex
      position="relative"
      onClick={onClick}
      onMouseDown={onMouseDown}
      cursor={onClick ? 'pointer' : 'default'}
      role="group"
    >
      {/* Current Version dot */}
      <Box
        position="absolute"
        left="17px"
        top={3}
        transform="translateX(-50%)"
        zIndex={2}
      >
        <TimelineDot isCurrentVersion />
      </Box>

      {/* Add checkpoint button */}
      {!isNextEventCheckpoint && (
        <Tooltip label={t('tooltip.add_checkpoint')}>
          <Box
            position="absolute"
            left="17px"
            cursor="pointer"
            top={2}
            transform="translateX(-50%)"
            zIndex={3}
            visibility={isAddingCheckpoint ? 'visible' : 'hidden'}
            opacity={isAddingCheckpoint ? 1 : 0}
            transition="opacity 0.2s"
            _groupHover={{ visibility: 'visible', opacity: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsAddingCheckpoint(true);
            }}
          >
            <Square
              size="20px"
              color={currentVersionColor}
              backgroundColor={previewBgColor}
              border="1px solid"
              style={{ rotate: '45deg' }}
            >
              <SparkelIcon
                icon={faPlus}
                color="inherit"
                size="xs"
                style={{ rotate: '-45deg' }}
              />
            </Square>
          </Box>
        </Tooltip>
      )}

      {/* Current Version content */}
      <Box
        width="100%"
        paddingLeft={9}
        paddingRight={4}
        paddingTop={showTopBorder ? '7px' : '9px'}
        paddingBottom={isLastItem && hasBorder ? '2px' : 0}
        backgroundColor={bgColor}
        borderTopRadius={showTopBorder ? 'lg' : 0}
        borderBottomRadius={isLastItem ? 'lg' : 0}
        borderTop={showTopBorder ? '2px solid' : 'none'}
        borderBottom={isLastItem && hasBorder ? '2px solid' : 'none'}
        borderColor={selectedBorderColor}
        _hover={
          !isSelected && !isInPreview
            ? { backgroundColor: hoverBgColor, borderRadius: 'lg' }
            : undefined
        }
        transition="background-color 0.2s"
      >
        {isAddingCheckpoint ? (
          <Box>
            <Input
              aria-label="Add checkpoint"
              type="text"
              textAlign={'start'}
              size="sm"
              px={1}
              borderRadius={0}
              borderColor="chakra-border-color"
              autoFocus={true}
              onFocus={(e) => e.target.select()}
              placeholder={t('rename.checkpoint_place_holder')}
              value={checkPointName}
              onChange={(e) => {
                setCheckpointName(e.target.value);
              }}
              onBlur={handleCheckPointCreation}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCheckPointCreation();
                } else if (e.key === 'Escape') {
                  setIsAddingCheckpoint(false);
                  setCheckpointName('');
                }
              }}
            />
          </Box>
        ) : (
          <Text fontWeight="bold" fontSize="sm" color={currentVersionColor}>
            {label}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default CurrentVersionHeader;
